<template>
  <div class="space-y-1">
    <label v-if="label" :for="id" class="block text-sm font-medium leading-5 text-dark-neutral-500">
      {{ label }}
      <span v-if="required" class="text-red-600">*</span>
    </label>
    <Combobox :model-value="modelValue" :nullable="nullable" @update:model-value="handleSelection">
      <div class="relative">
        <ComboboxInput
          :id="id"
          :placeholder="placeholder"
          :disabled="disabled"
          :display-value="getDisplayValue"
          autocomplete="off"
          class="w-full focus:outline-hidden disabled:bg-transparent font-normal text-gray-900 text-sm px-3.5 py-2.5 flex items-center justify-between rounded-md bg-white border border-slate-300"
          :class="[
            inputClasses,
            {
              'bg-white hover:border-primary-500 focus:border-primary-500 border-mid-neutral-100 ring-primary-500':
                !disabled,
              'bg-black/5': disabled,
            },
          ]"
          @change="handleInputChange"
          @blur="emit('blur', modelValue)"
          @keydown.enter="emit('enter', modelValue)"
        />
        <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
        </ComboboxButton>

        <ComboboxOptions
          class="p-1 z-50 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
        >
          <div
            v-if="filteredItems.length === 0 && !currentQuery"
            class="relative cursor-default select-none py-2 px-4 text-gray-500"
          >
            {{ emptyMessage }}
          </div>

          <ComboboxOption
            v-if="showCreateOption"
            v-slot="{ active }"
            :value="createOptionValue"
            as="template"
          >
            <li
              class="relative cursor-default select-none py-2 px-4 rounded"
              :class="{
                'bg-primary-500 text-white': active,
                'text-gray-900': !active,
              }"
            >
              <span class="block truncate">
                {{ createMessage(currentQuery) }}
              </span>
            </li>
          </ComboboxOption>

          <ComboboxOption
            v-for="item in filteredItems"
            :key="computeItemKey(item)"
            v-slot="{ selected, active }"
            :value="item"
            as="template"
          >
            <li
              class="relative cursor-default select-none py-2 px-4 rounded"
              :class="{
                'bg-primary-500 text-white': active,
                'text-gray-900': !active,
              }"
            >
              <slot name="option" :item="item" :selected="selected" :active="active">
                <span
                  class="block truncate"
                  :class="{
                    'font-medium': selected,
                    'font-normal': !selected,
                  }"
                >
                  {{ displayFn(item) }}
                </span>
              </slot>
              <span
                v-if="selected"
                class="absolute inset-y-0 right-0 flex items-center pr-3"
                :class="{ 'text-white': active, 'text-primary-500': !active }"
              >
                <CheckIcon class="h-5 w-5" aria-hidden="true" />
              </span>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </div>
    </Combobox>
  </div>
</template>

<script setup lang="ts" generic="T extends string | number | boolean | object">
import { shallowRef } from "vue";
import { v4 as uuidv4 } from "uuid";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from "@headlessui/vue";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/vue/20/solid";

export interface NewItemPayload {
  __isNew: true;
  value: string;
}

const props = withDefaults(
  defineProps<{
    items: T[];
    displayFn: (item: T) => string;
    filterFn?: (item: T, query: string) => boolean;
    keyFn?: (item: T) => string | number;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    nullable?: boolean;
    allowCreate?: boolean;
    createMessage?: (query: string) => string;
    emptyMessage?: string;
    inputClasses?: string;
  }>(),
  {
    label: "",
    placeholder: "",
    disabled: false,
    required: false,
    nullable: false,
    allowCreate: false,
    createMessage: (query: string) => `Create "${query}"`,
    emptyMessage: "No items available",
    inputClasses: "",
    filterFn: undefined,
    keyFn: undefined,
  },
);

const emit = defineEmits<{
  create: [value: string];
  blur: [value: T | null | undefined];
  enter: [value: T | null | undefined];
}>();

const modelValue = defineModel<T | null>();

const id = uuidv4();

const currentQuery = shallowRef("");
const filteredItems = shallowRef<T[]>(props.items || []);
const showCreateOption = shallowRef(false);
const createOptionValue = shallowRef<NewItemPayload>({
  __isNew: true,
  value: "",
});

function handleInputChange(event: Event) {
  const newQuery = (event.target as HTMLInputElement).value;
  currentQuery.value = newQuery;
  createOptionValue.value = { __isNew: true, value: newQuery };

  const items = props.items;
  if (!items || items.length === 0) {
    filteredItems.value = [];
    showCreateOption.value = props.allowCreate && !!newQuery;
    return;
  }

  if (!newQuery) {
    filteredItems.value = items;
    showCreateOption.value = false;
    return;
  }

  const normalizedQuery = newQuery.toLowerCase().trim();

  if (props.filterFn) {
    filteredItems.value = items.filter((item) => props.filterFn!(item, normalizedQuery));
  } else {
    filteredItems.value = items.filter((item) => {
      try {
        return props.displayFn(item).toLowerCase().includes(normalizedQuery);
      } catch {
        return false;
      }
    });
  }

  const exactMatchExists = items.some((item) => {
    try {
      return props.displayFn(item).toLowerCase() === normalizedQuery;
    } catch {
      return false;
    }
  });

  showCreateOption.value = props.allowCreate && !!newQuery && !exactMatchExists;
}

function getDisplayValue(item: unknown): string {
  if (item == null) return "";
  if (typeof item === "string") return item;
  if (typeof item === "number" || typeof item === "boolean") return String(item);
  try {
    return props.displayFn(item as T);
  } catch {
    return "";
  }
}

function computeItemKey(item: T): string | number {
  if (props.keyFn) {
    return props.keyFn(item);
  }
  try {
    return props.displayFn(item);
  } catch {
    return String(item);
  }
}

function handleSelection(value: T | NewItemPayload | null) {
  if (value && typeof value === "object" && "__isNew" in value) {
    emit("create", value.value);
    currentQuery.value = "";
    filteredItems.value = props.items || [];
    showCreateOption.value = false;
    return;
  }
  modelValue.value = value as T | null;
  currentQuery.value = "";
  filteredItems.value = props.items || [];
  showCreateOption.value = false;
}
</script>
